/**
 * WysiwygPreviewEditor - A4 預覽面板的 WYSIWYG 編輯器
 *
 * 可直接在預覽面板中編輯，附帶格式化工具欄
 * 類似 Discourse 的編輯體驗
 *
 * @module editor/components/WysiwygPreviewEditor
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import {
  Editor,
  rootCtx,
  defaultValueCtx,
  editorViewOptionsCtx,
  editorViewCtx,
} from '@milkdown/core';
import { Milkdown, MilkdownProvider, useEditor, useInstance } from '@milkdown/react';
import {
  commonmark,
  toggleStrongCommand,
  toggleEmphasisCommand,
  wrapInBlockquoteCommand,
  insertHrCommand,
} from '@milkdown/preset-commonmark';
import { gfm, toggleStrikethroughCommand } from '@milkdown/preset-gfm';
import { listener, listenerCtx } from '@milkdown/plugin-listener';
import { emoji, emojiSchema } from '@milkdown/plugin-emoji';
import { getMarkdown, replaceAll, callCommand, $view } from '@milkdown/utils';
import { codeBlockSchema } from '@milkdown/preset-commonmark';
import twemoji from '@twemoji/api';
import EmojiPicker, { EmojiStyle, EmojiClickData } from 'emoji-picker-react';
import {
  Bold,
  Italic,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Minus,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Link,
  Smile,
} from 'lucide-react';
import '@milkdown/theme-nord/style.css';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import type { Node as ProseMirrorNode } from 'prosemirror-model';
import type { RoomParticipant } from '@linkode/shared';
import { PRESENCE_COLORS } from '../cm/remoteCursor';

// ============================================================================
// Types
// ============================================================================

export interface WysiwygPreviewEditorProps {
  /** 初始 Markdown 內容 */
  initialContent?: string;
  /** 是否為唯讀模式 */
  readonly?: boolean;
  /** 內容變更回調 */
  onChange?: (markdown: string) => void;
  /** 遠端參與者（顯示游標/選取） */
  participants?: RoomParticipant[];
  /** 是否顯示工具欄 */
  showToolbar?: boolean;
  /** 自訂 className */
  className?: string;
  /** 自訂樣式 */
  style?: React.CSSProperties;
}

export interface WysiwygPreviewEditorRef {
  /** 取得 Markdown 內容 */
  getMarkdown(): string;
  /** 設定 Markdown 內容 */
  setMarkdown(content: string): void;
  /** 聚焦編輯器 */
  focus(): void;
}

// ============================================================================
// Toolbar Component
// ============================================================================

interface ToolbarProps {
  onCommand: (command: string) => void;
  disabled?: boolean;
}

const Toolbar: React.FC<ToolbarProps> = ({ onCommand, disabled }) => {
  const toolbarStyle: React.CSSProperties = {
    display: 'flex',
    gap: '2px',
    padding: '8px 12px',
    backgroundColor: 'var(--color-background-light, #f9fafb)',
    borderBottom: '1px solid var(--border-color, #e5e7eb)',
    flexWrap: 'wrap',
    alignItems: 'center',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'background-color 0.15s',
  };

  const separatorStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'var(--border-color, #e5e7eb)',
    margin: '0 6px',
  };

  const handleClick = (command: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (!disabled) {
      onCommand(command);
    }
  };

  return (
    <div style={toolbarStyle} data-testid="wysiwyg-toolbar">
      {/* 格式化 */}
      <button
        style={buttonStyle}
        onClick={handleClick('bold')}
        title="粗體 (Ctrl+B)"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Bold size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('italic')}
        title="斜體 (Ctrl+I)"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Italic size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('strikethrough')}
        title="刪除線"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Strikethrough size={18} />
      </button>

      <div style={separatorStyle} />

      {/* 標題 */}
      <button
        style={buttonStyle}
        onClick={handleClick('heading1')}
        title="標題 1"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Heading1 size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('heading2')}
        title="標題 2"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Heading2 size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('heading3')}
        title="標題 3"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Heading3 size={18} />
      </button>

      <div style={separatorStyle} />

      {/* 列表 */}
      <button
        style={buttonStyle}
        onClick={handleClick('bulletList')}
        title="項目列表"
        disabled={disabled}
        className="toolbar-btn"
      >
        <List size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('orderedList')}
        title="編號列表"
        disabled={disabled}
        className="toolbar-btn"
      >
        <ListOrdered size={18} />
      </button>

      <div style={separatorStyle} />

      {/* 區塊 */}
      <button
        style={buttonStyle}
        onClick={handleClick('blockquote')}
        title="引用"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Quote size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('code')}
        title="程式碼"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Code size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('hr')}
        title="分隔線"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Minus size={18} />
      </button>

      <div style={separatorStyle} />

      {/* 插入 */}
      <button
        style={buttonStyle}
        onClick={handleClick('link')}
        title="插入連結"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Link size={18} />
      </button>
      <button
        style={buttonStyle}
        onClick={handleClick('emoji')}
        title="插入表情符號"
        disabled={disabled}
        className="toolbar-btn"
      >
        <Smile size={18} />
      </button>
    </div>
  );
};

// ============================================================================
// Global Event Delegation for Code Block Copy Button
// ============================================================================
// Strategy: Handle copy button clicks at document level to completely bypass
// ProseMirror's event handling. This solves the issue where ProseMirror
// re-renders NodeView during mousedown, causing button detachment.
//
// Key insight: ProseMirror continuously re-renders NodeView, so we cannot
// persist innerHTML changes. Instead, we use CSS to control icon visibility
// via data attributes that survive re-renders through polling.
// ============================================================================

const setupGlobalCopyHandler = (): (() => void) => {
  // Global state: track which code blocks are in "copied" or "failed" state
  // Key: code text hash (first 50 chars), Value: { state, expireAt }
  const buttonStates = new Map<string, { state: 'copied' | 'failed'; expireAt: number }>();

  // Generate a simple key from code content
  const getCodeKey = (pre: Element): string => {
    const code = pre.querySelector('code');
    const text = code?.textContent || '';
    return text.substring(0, 50);
  };

  // Apply state to a button using data attribute (CSS will handle icon visibility)
  const applyButtonState = (btn: HTMLButtonElement, state: 'copied' | 'failed' | 'default') => {
    if (state === 'default') {
      btn.removeAttribute('data-state');
      btn.classList.remove('copied', 'failed');
    } else {
      btn.setAttribute('data-state', state);
      btn.classList.add(state);
      btn.classList.remove(state === 'copied' ? 'failed' : 'copied');
    }
  };

  // Polling function to re-apply states (runs only when there are active states)
  let pollingInterval: ReturnType<typeof setInterval> | null = null;

  const startPolling = () => {
    if (pollingInterval) return;
    pollingInterval = setInterval(() => {
      const now = Date.now();
      let hasActiveStates = false;

      document.querySelectorAll('.code-block-copy-btn').forEach((btn) => {
        const pre = btn.closest('pre');
        if (!pre) return;

        const key = getCodeKey(pre);
        const stateInfo = buttonStates.get(key);

        if (stateInfo && stateInfo.expireAt > now) {
          hasActiveStates = true;
          // Always apply state (ProseMirror may have recreated the button)
          // Remove the condition check - always apply to handle ProseMirror re-renders
          applyButtonState(btn as HTMLButtonElement, stateInfo.state);
        } else if (stateInfo) {
          // Expired, clean up
          buttonStates.delete(key);
          applyButtonState(btn as HTMLButtonElement, 'default');
        }
      });

      // Stop polling if no active states
      if (!hasActiveStates && buttonStates.size === 0) {
        if (pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
        }
      }
    }, 30); // Check every 30ms for faster response to ProseMirror re-renders
  };

  const handleCopyClick = async (e: PointerEvent) => {
    const target = e.target as HTMLElement;
    const copyBtn = target.closest('.code-block-copy-btn') as HTMLButtonElement;
    if (!copyBtn) return;

    // Prevent ALL event propagation immediately
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Find the code element
    const pre = copyBtn.closest('pre');
    if (!pre) return;

    const code = pre.querySelector('code');
    const codeText = code?.textContent || '';
    const key = getCodeKey(pre);

    try {
      await navigator.clipboard.writeText(codeText);
      // Set state with 2 second expiration
      buttonStates.set(key, { state: 'copied', expireAt: Date.now() + 2000 });
      applyButtonState(copyBtn, 'copied');
      startPolling();

      // Clear state after 2 seconds
      setTimeout(() => {
        buttonStates.delete(key);
      }, 2100);
    } catch (err) {
      console.error('Failed to copy code:', err);
      buttonStates.set(key, { state: 'failed', expireAt: Date.now() + 2000 });
      applyButtonState(copyBtn, 'failed');
      startPolling();

      setTimeout(() => {
        buttonStates.delete(key);
      }, 2100);
    }
  };

  // Use pointerdown - fires before mousedown and is more reliable
  document.addEventListener('pointerdown', handleCopyClick, true);

  // Return cleanup function
  return () => {
    document.removeEventListener('pointerdown', handleCopyClick, true);
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
    buttonStates.clear();
  };
};

// ============================================================================
// Custom Code Block View with Copy Button
// ============================================================================

const codeBlockView = $view(codeBlockSchema.node, () => {
  return (node) => {
    // Pre element (this will contain the code and button)
    const pre = document.createElement('pre');
    pre.style.position = 'relative';

    const language = node.attrs.language || '';
    if (language) {
      pre.setAttribute('data-language', language);
    }

    // Copy button - attached INSIDE pre element
    // Event handling is done via global delegation (setupGlobalCopyHandler)
    // Contains both copy and check icons, CSS controls visibility via data-state
    const copyBtn = document.createElement('button');
    copyBtn.className = 'code-block-copy-btn';
    copyBtn.title = '複製代碼';
    copyBtn.type = 'button';
    copyBtn.setAttribute('contenteditable', 'false');
    // Both icons: copy icon (default visible) and check icon (visible when data-state="copied")
    copyBtn.innerHTML = `
      <svg class="icon-copy" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
      <svg class="icon-check" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      <svg class="icon-x" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
    `;
    copyBtn.style.cssText = `
      position: absolute;
      top: 8px;
      right: 8px;
      background-color: transparent;
      border: none;
      border-radius: 4px;
      padding: 6px;
      color: #9ca3af;
      cursor: pointer;
      transition: all 0.2s;
      z-index: 1000;
      pointer-events: auto;
      user-select: none;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Code element - this is the contentDOM where ProseMirror renders content
    const code = document.createElement('code');

    // Assemble: pre contains both button and code
    pre.appendChild(copyBtn);
    pre.appendChild(code);

    // Helper to check if target is the copy button
    const isCopyBtn = (target: EventTarget | null): boolean => {
      return target === copyBtn || (target instanceof Node && copyBtn.contains(target));
    };

    return {
      dom: pre,
      contentDOM: code,
      update: (updatedNode) => {
        if (updatedNode.type.name !== 'code_block') {
          return false;
        }
        const newLanguage = updatedNode.attrs.language || '';
        if (newLanguage) {
          pre.setAttribute('data-language', newLanguage);
        } else {
          pre.removeAttribute('data-language');
        }
        return true;
      },
      stopEvent: (event) => {
        // Stop ProseMirror from handling button events
        // The actual click handling is done by global delegation
        if (isCopyBtn(event.target)) {
          return true;
        }
        return false;
      },
      ignoreMutation: (mutation) => {
        // Tell ProseMirror to ignore DOM mutations on the copy button
        // This prevents ProseMirror from re-rendering the NodeView when
        // we change the button's data-state attribute or classList
        if (mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement;
          // Ignore attribute changes on the button or its children (SVG icons)
          if (isCopyBtn(target) || copyBtn.contains(target)) {
            return true;
          }
        }
        if (mutation.type === 'childList') {
          // Ignore child changes within the button (shouldn't happen, but be safe)
          if (isCopyBtn(mutation.target) || copyBtn.contains(mutation.target as Node)) {
            return true;
          }
        }
        // Let ProseMirror handle other mutations (like code content changes)
        return false;
      },
    };
  };
});

// ============================================================================
// Remote Presence (ProseMirror Decorations)
// ============================================================================

interface RemotePresenceState {
  decorations: DecorationSet;
}

interface RemotePresenceMeta {
  participants?: RoomParticipant[];
}

const remotePresenceKey = new PluginKey<RemotePresenceState>('remote-presence');

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const hexToRgba = (hex: string, alpha: number): string => {
  const sanitized = hex.replace('#', '');
  const bigint = parseInt(sanitized, 16);
  const r = sanitized.length === 3 ? (bigint >> 8) * 17 : (bigint >> 16) & 255;
  const g = sanitized.length === 3 ? ((bigint >> 4) & 15) * 17 : (bigint >> 8) & 255;
  const b = sanitized.length === 3 ? (bigint & 15) * 17 : bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const posFromLineColumn = (doc: ProseMirrorNode, line: number, column: number): number | null => {
  const text = doc.textBetween(0, doc.content.size, '\n', '\n');
  const lines = text.split('\n');
  if (line < 0 || line >= lines.length) return null;
  const currentLine = lines[line];
  if (currentLine === undefined) return null;
  const clampedColumn = clamp(column, 0, currentLine.length);
  let offset = clampedColumn;
  for (let i = 0; i < line; i += 1) {
    const lineContent = lines[i];
    if (lineContent === undefined) return null;
    offset += lineContent.length + 1; // +1 for newline separator
  }
  return clamp(offset + 1, 0, doc.content.size);
};

const buildRemoteDecorations = (
  doc: ProseMirrorNode,
  participants: RoomParticipant[]
): DecorationSet => {
  const decorations: Decoration[] = [];

  participants.forEach((p) => {
    const colorIndex = p.presence.colorId % PRESENCE_COLORS.length;
    const color = PRESENCE_COLORS[colorIndex] ?? '#6b7280'; // fallback to gray

    if (p.presence.selection) {
      const { anchor, head } = p.presence.selection;
      const from = posFromLineColumn(doc, anchor.line, anchor.column);
      const to = posFromLineColumn(doc, head.line, head.column);
      if (from !== null && to !== null && from !== to) {
        const start = Math.min(from, to);
        const end = Math.max(from, to);
        decorations.push(
          Decoration.inline(start, end, {
            class: 'pm-remote-selection',
            'data-user-id': p.userId,
            style: `background-color: ${hexToRgba(color, 0.25)};`,
          })
        );
      }
    }

    if (p.presence.cursor) {
      const cursorPos = posFromLineColumn(doc, p.presence.cursor.line, p.presence.cursor.column);
      if (cursorPos !== null) {
        decorations.push(
          Decoration.widget(
            cursorPos,
            () => {
              const container = document.createElement('span');
              container.className = 'pm-remote-cursor';
              container.setAttribute('data-user-id', p.userId);

              const lineEl = document.createElement('span');
              lineEl.className = 'pm-remote-cursor-line';
              lineEl.style.backgroundColor = color;

              const label = document.createElement('span');
              label.className = 'pm-remote-cursor-label';
              label.textContent = p.presence.displayName;
              label.style.backgroundColor = color;

              container.appendChild(lineEl);
              container.appendChild(label);
              return container;
            },
            { key: `remote-cursor-${p.userId}` }
          )
        );
      }
    }
  });

  return DecorationSet.create(doc, decorations);
};

const createRemotePresencePlugin = () =>
  new Plugin<RemotePresenceState>({
    key: remotePresenceKey,
    state: {
      init: (_, state) => ({ decorations: DecorationSet.create(state.doc, []) }),
      apply: (tr, pluginState) => {
        let decorations = pluginState.decorations;
        const meta = tr.getMeta(remotePresenceKey) as RemotePresenceMeta | undefined;

        if (meta?.participants) {
          decorations = buildRemoteDecorations(tr.doc, meta.participants);
        } else if (tr.docChanged) {
          decorations = decorations.map(tr.mapping, tr.doc);
        }

        return { decorations };
      },
    },
    props: {
      decorations: (state) => remotePresenceKey.getState(state)?.decorations,
    },
  });

// ============================================================================
// Inner Editor Component
// ============================================================================

interface InnerEditorProps {
  initialContent: string;
  readonly: boolean;
  onChange?: (markdown: string) => void;
  editorRef: React.MutableRefObject<Editor | null>;
  onEditorReady?: () => void;
  participants: RoomParticipant[];
}

const InnerEditor: React.FC<InnerEditorProps> = ({
  initialContent,
  readonly,
  onChange,
  editorRef,
  onEditorReady,
  participants,
}) => {
  // Setup global copy handler for code blocks
  useEffect(() => {
    const cleanup = setupGlobalCopyHandler();
    return cleanup;
  }, []);

  const { get } = useEditor(
    (root) =>
      Editor.make()
        .config((ctx) => {
          ctx.set(rootCtx, root);
          ctx.set(defaultValueCtx, initialContent);
          ctx.update(editorViewOptionsCtx, (prev) => ({
            ...prev,
            editable: () => !readonly,
          }));
          ctx.get(listenerCtx).markdownUpdated((_ctx, markdown, prevMarkdown) => {
            // 前端樂觀同步：立即觸發 onChange，無防抖
            // 後端儲存的防抖由 SaveQueueManager (2000ms) 負責
            if (markdown !== prevMarkdown && onChange) {
              onChange(markdown);
            }
          });
        })
        .use(commonmark)
        .use(codeBlockView)
        .use(gfm)
        .use(listener)
        .use(emoji),
    [readonly]
  );

  useEffect(() => {
    const editor = get();
    if (editor) {
      editorRef.current = editor;
      editor.action((ctx) => {
        const view = ctx.get(editorViewCtx);
        const hasRemotePresence = view.state.plugins.some((p) => p.spec.key === remotePresenceKey);
        if (!hasRemotePresence) {
          const plugin = createRemotePresencePlugin();
          const state = view.state.reconfigure({ plugins: [...view.state.plugins, plugin] });
          view.updateState(state);
        }
        if (participants.length > 0) {
          const tr = view.state.tr.setMeta(remotePresenceKey, { participants });
          view.dispatch(tr);
        }
      });
      onEditorReady?.();
    }
  }, [get, editorRef, onEditorReady, participants]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.action((ctx) => {
      const view = ctx.get(editorViewCtx);
      if (!view.state.plugins.some((p) => p.spec.key === remotePresenceKey)) return;
      const tr = view.state.tr.setMeta(remotePresenceKey, { participants });
      view.dispatch(tr);
    });
  }, [participants]);

  return <Milkdown />;
};

// ============================================================================
// Toolbar Controller (uses Milkdown context)
// ============================================================================

interface ToolbarControllerProps {
  showToolbar: boolean;
  readonly: boolean;
}

const ToolbarController: React.FC<ToolbarControllerProps> = ({ showToolbar, readonly }) => {
  const [, getInstance] = useInstance();
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  const handleCommand = useCallback(
    (command: string) => {
      const editor = getInstance();
      if (!editor) return;

      try {
        switch (command) {
          case 'bold':
            editor.action(callCommand(toggleStrongCommand.key));
            break;
          case 'italic':
            editor.action(callCommand(toggleEmphasisCommand.key));
            break;
          case 'strikethrough':
            editor.action(callCommand(toggleStrikethroughCommand.key));
            break;
          case 'blockquote':
            editor.action(callCommand(wrapInBlockquoteCommand.key));
            break;
          case 'hr':
            editor.action(callCommand(insertHrCommand.key));
            break;
          case 'heading1':
          case 'heading2':
          case 'heading3':
            // 透過 ProseMirror 直接操作
            editor.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              const level = parseInt(command.replace('heading', ''));
              const { state, dispatch } = view;
              const { $from } = state.selection;
              const lineStart = $from.start();
              const lineEnd = $from.end();
              const lineText = state.doc.textBetween(lineStart, lineEnd);

              // 移除現有的標題符號
              const cleanText = lineText.replace(/^#+\s*/, '');
              const newText = '#'.repeat(level) + ' ' + cleanText;

              dispatch(state.tr.replaceWith(lineStart, lineEnd, state.schema.text(newText)));
            });
            break;
          case 'bulletList':
            editor.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              const { state, dispatch } = view;
              const { $from } = state.selection;
              const lineStart = $from.start();
              dispatch(state.tr.insertText('- ', lineStart));
            });
            break;
          case 'orderedList':
            editor.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              const { state, dispatch } = view;
              const { $from } = state.selection;
              const lineStart = $from.start();
              dispatch(state.tr.insertText('1. ', lineStart));
            });
            break;
          case 'code':
            editor.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              const { state, dispatch } = view;
              const { from, to } = state.selection;
              if (from === to) {
                // 無選取，插入程式碼區塊
                dispatch(state.tr.insertText('```\n\n```', from));
              } else {
                // 有選取，包裹為行內程式碼
                const selectedText = state.doc.textBetween(from, to);
                dispatch(
                  state.tr.replaceWith(from, to, state.schema.text('`' + selectedText + '`'))
                );
              }
            });
            break;
          case 'link':
            editor.action((ctx) => {
              const view = ctx.get(editorViewCtx);
              const { state, dispatch } = view;
              const { from, to } = state.selection;
              const selectedText = from !== to ? state.doc.textBetween(from, to) : '連結文字';
              const linkText = `[${selectedText}](https://)`;
              dispatch(state.tr.replaceWith(from, to, state.schema.text(linkText)));
            });
            break;
          case 'emoji':
            setEmojiPickerOpen(!emojiPickerOpen);
            break;
        }
      } catch (error) {
        console.warn('Command failed:', command, error);
      }
    },
    [getInstance, emojiPickerOpen]
  );

  if (!showToolbar || readonly) return null;

  return (
    <>
      <Toolbar onCommand={handleCommand} disabled={readonly} />
      {emojiPickerOpen && (
        <EmojiPickerComponent
          onSelect={(selectedEmoji) => {
            const editor = getInstance();
            if (editor) {
              editor.action((ctx) => {
                const view = ctx.get(editorViewCtx);
                const { state, dispatch } = view;

                // 使用 twemoji.parse 將 Unicode emoji 轉換為 Twemoji HTML
                const html = twemoji.parse(selectedEmoji, {
                  base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji/assets/',
                  attributes: (text: string) => ({ title: text }),
                });

                // 創建 emoji node 並插入
                const emojiNode = emojiSchema.type(ctx).create({ html });
                dispatch(state.tr.replaceSelectionWith(emojiNode).scrollIntoView());
              });
            }
            setEmojiPickerOpen(false);
          }}
          onClose={() => setEmojiPickerOpen(false)}
        />
      )}
    </>
  );
};

// ============================================================================
// Emoji Picker with emoji-picker-react (Twemoji Support)
// ============================================================================

interface EmojiPickerComponentProps {
  onSelect: (emoji: string) => void;
  onClose: () => void;
}

const EmojiPickerComponent: React.FC<EmojiPickerComponentProps> = ({ onSelect, onClose }) => {
  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      onSelect(emojiData.emoji);
    },
    [onSelect]
  );

  return (
    <>
      <div className="emoji-picker-overlay" onClick={onClose} aria-hidden="true" />
      <div className="emoji-picker-container" role="dialog" aria-label="Emoji picker">
        <EmojiPicker
          onEmojiClick={handleEmojiClick}
          emojiStyle={EmojiStyle.TWITTER}
          skinTonesDisabled={false}
          searchPlaceHolder="Search emoji..."
          previewConfig={{ showPreview: false }}
          height={350}
          width={320}
        />
      </div>
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const WysiwygPreviewEditor = forwardRef<WysiwygPreviewEditorRef, WysiwygPreviewEditorProps>(
  function WysiwygPreviewEditor(props, ref) {
    const {
      initialContent = '',
      readonly = false,
      onChange,
      participants = [],
      showToolbar = true,
      className,
      style,
    } = props;

    const editorRef = useRef<Editor | null>(null);
    const [isReady, setIsReady] = useState(false);

    // 🛡️ 防止死循環：追蹤更新狀態和上次內容
    const isUpdatingRef = useRef(false);
    const lastContentRef = useRef(initialContent);

    useImperativeHandle(
      ref,
      () => ({
        getMarkdown(): string {
          const editor = editorRef.current;
          if (!editor) return '';
          try {
            return editor.action(getMarkdown());
          } catch {
            return '';
          }
        },

        setMarkdown(content: string): void {
          const editor = editorRef.current;
          if (!editor) return;

          // 🛡️ 防護 1: 內容相同則跳過
          if (lastContentRef.current === content) {
            return;
          }

          try {
            isUpdatingRef.current = true; // 設定更新標記
            editor.action(replaceAll(content));
            lastContentRef.current = content;

            // 使用 requestAnimationFrame 延遲重置
            requestAnimationFrame(() => {
              isUpdatingRef.current = false;
            });
          } catch {
            isUpdatingRef.current = false;
          }
        },

        focus(): void {
          const editorDom = document.querySelector('.wysiwyg-preview-editor .milkdown .editor');
          if (editorDom instanceof HTMLElement) {
            editorDom.focus();
          }
        },
      }),
      []
    );

    const handleChange = useCallback(
      (markdown: string) => {
        // 🛡️ 防護 2: 正在更新中則跳過 onChange
        if (isUpdatingRef.current) {
          return;
        }

        lastContentRef.current = markdown;
        onChange?.(markdown);
      },
      [onChange]
    );

    // 🔄 響應式更新：監聽 initialContent 變化
    useEffect(() => {
      if (isReady && editorRef.current && initialContent !== lastContentRef.current) {
        isUpdatingRef.current = true;
        try {
          editorRef.current.action(replaceAll(initialContent));
          lastContentRef.current = initialContent;
        } catch {
          // 忽略錯誤
        } finally {
          requestAnimationFrame(() => {
            isUpdatingRef.current = false;
          });
        }
      }
    }, [initialContent, isReady]);

    const containerStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      ...style,
    };

    const editorContainerStyle: React.CSSProperties = {
      flex: 1,
      overflow: 'auto',
    };

    return (
      <div
        className={`wysiwyg-preview-editor ${className || ''}`}
        style={containerStyle}
        data-testid="wysiwyg-preview-editor"
      >
        <MilkdownProvider>
          <ToolbarController showToolbar={showToolbar && isReady} readonly={readonly} />
          <div style={editorContainerStyle}>
            <InnerEditor
              initialContent={initialContent}
              readonly={readonly}
              onChange={handleChange}
              editorRef={editorRef}
              onEditorReady={() => setIsReady(true)}
              participants={participants}
            />
          </div>
        </MilkdownProvider>
      </div>
    );
  }
);

// ============================================================================
// Styles
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'wysiwyg-preview-editor-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .wysiwyg-preview-editor .milkdown {
        height: 100%;
        min-height: 100%;
      }

      .wysiwyg-preview-editor .milkdown .editor {
        outline: none;
        padding: 40px;
        min-height: 100%;
      }

      .toolbar-btn:hover {
        background-color: var(--color-primary-light, #e0e7ff) !important;
      }

      .toolbar-btn:active {
        background-color: var(--color-primary, #6366f1) !important;
        color: white !important;
      }

      .wysiwyg-preview-editor .pm-remote-cursor {
        position: relative;
        display: inline-block;
        width: 0;
        height: 1.2em;
        pointer-events: none;
      }

      .wysiwyg-preview-editor .pm-remote-cursor-line {
        position: absolute;
        left: 0;
        top: 0;
        width: 2px;
        height: 1.2em;
        border-radius: 1px;
        animation: cm-cursor-blink 1s ease-in-out infinite;
      }

      .wysiwyg-preview-editor .pm-remote-cursor-label {
        position: absolute;
        left: 0;
        bottom: 100%;
        margin-bottom: 2px;
        padding: 2px 6px;
        font-size: 10px;
        font-weight: 500;
        line-height: 1.2;
        white-space: nowrap;
        color: white;
        border-radius: 3px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        opacity: 0.9;
        z-index: 10;
      }

      .wysiwyg-preview-editor .pm-remote-selection {
        border-radius: 2px;
      }

      /* Code Block Copy Button - SVG icon with border and background */
      /* Button visibility: hidden by default, show on pre:hover */
      .code-block-copy-btn {
        opacity: 0;
        transition: opacity 0.15s ease-in-out;
      }
      pre:hover .code-block-copy-btn {
        opacity: 1;
      }
      
      /* Icon fade transition */
      .code-block-copy-btn .icon-copy,
      .code-block-copy-btn .icon-check,
      .code-block-copy-btn .icon-x {
        transition: opacity 0.15s ease-in-out;
      }
      
      .code-block-copy-btn .icon-copy { opacity: 1; }
      .code-block-copy-btn .icon-check { opacity: 0; position: absolute; }
      .code-block-copy-btn .icon-x { opacity: 0; position: absolute; }
      
      .code-block-copy-btn[data-state="copied"] .icon-copy { opacity: 0; }
      .code-block-copy-btn[data-state="copied"] .icon-check { opacity: 1; }
      .code-block-copy-btn[data-state="copied"] .icon-x { opacity: 0; }
      
      .code-block-copy-btn[data-state="failed"] .icon-copy { opacity: 0; }
      .code-block-copy-btn[data-state="failed"] .icon-check { opacity: 0; }
      .code-block-copy-btn[data-state="failed"] .icon-x { opacity: 1; }
      
      .code-block-copy-btn:hover {
        color: inherit;
      }

      /* Emoji Picker Styles */
      .emoji-picker-overlay {
        position: fixed;
        inset: 0;
        z-index: 99;
        background: transparent;
      }

      .emoji-picker-container {
        position: absolute;
        top: 48px;
        right: 12px;
        z-index: 100;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }

      .emoji-picker-container em-emoji-picker {
        --em-rgb-background: 255, 255, 255;
        --em-rgb-input: 249, 250, 251;
        --em-rgb-accent: 99, 102, 241;
      }

      /* Emoji Plugin Styles - Milkdown Twemoji */
      .wysiwyg-preview-editor .milkdown span[data-type="emoji"] {
        display: inline-block;
        vertical-align: middle;
      }

      .wysiwyg-preview-editor .milkdown span[data-type="emoji"] img {
        width: 1.2em;
        height: 1.2em;
        vertical-align: -0.2em;
        display: inline-block;
        margin: 0 0.05em;
      }

      .wysiwyg-preview-editor .milkdown .emoji {
        width: 1.2em;
        height: 1.2em;
        vertical-align: -0.2em;
        display: inline-block;
        margin: 0 0.05em;
      }

      /* A4 頁面樣式整合 */
      .wysiwyg-preview-editor .milkdown h1 {
        font-size: 2em;
        font-weight: 700;
        margin-top: 0.67em;
        margin-bottom: 0.67em;
        line-height: 1.2;
        color: var(--color-text-primary, #1f2937);
      }

      .wysiwyg-preview-editor .milkdown h2 {
        font-size: 1.5em;
        font-weight: 700;
        margin-top: 0.83em;
        margin-bottom: 0.83em;
        line-height: 1.3;
        color: var(--color-text-primary, #1f2937);
      }

      .wysiwyg-preview-editor .milkdown h3 {
        font-size: 1.17em;
        font-weight: 700;
        margin-top: 1em;
        margin-bottom: 1em;
        line-height: 1.4;
        color: var(--color-text-primary, #1f2937);
      }

      .wysiwyg-preview-editor .milkdown p {
        margin-top: 1em;
        margin-bottom: 1em;
        line-height: 1.8;
        color: var(--color-text-primary, #1f2937);
      }

      .wysiwyg-preview-editor .milkdown blockquote {
        border-left: 4px solid var(--color-primary, #6366f1);
        padding-left: 16px;
        margin-left: 0;
        margin-right: 0;
        color: var(--color-text-secondary, #6b7280);
        font-style: italic;
      }

      /* Code Block with Language Label (CSS-Only using Milkdown's data-language attribute) */
      .wysiwyg-preview-editor .milkdown pre[data-language] {
        position: relative;
        background-color: #282c34;
        padding: 48px 16px 16px 16px;
        margin: 12px 0;
        border-radius: 6px;
        overflow: hidden;
        border: 1px solid #374151;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.875em;
      }

      .wysiwyg-preview-editor .milkdown pre[data-language]::before {
        content: attr(data-language);
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        background-color: #1f2937;
        color: #9ca3af;
        padding: 8px 16px;
        font-size: 0.75em;
        font-family: ui-monospace, monospace;
        font-weight: bold;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        border-bottom: 1px solid #374151;
      }

      .wysiwyg-preview-editor .milkdown pre[data-language] code {
        background-color: transparent;
        padding: 0;
        color: #abb2bf;
      }

      /* Code Block without language (fallback) */
      .wysiwyg-preview-editor .milkdown pre:not([data-language]) {
        background-color: var(--color-background-light, #f9fafb);
        padding: 16px;
        border-radius: 8px;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.875em;
        overflow: auto;
        border: 1px solid var(--border-color, #e5e7eb);
      }

      .wysiwyg-preview-editor .milkdown code {
        background-color: var(--color-background-light, #f9fafb);
        padding: 2px 6px;
        border-radius: 4px;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.875em;
      }

      .wysiwyg-preview-editor .milkdown pre code {
        background-color: transparent;
        padding: 0;
        color: #abb2bf;
      }

      .wysiwyg-preview-editor .milkdown ul,
      .wysiwyg-preview-editor .milkdown ol {
        padding-left: 24px;
        margin-top: 1em;
        margin-bottom: 1em;
      }

      .wysiwyg-preview-editor .milkdown li {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
        line-height: 1.8;
      }

      .wysiwyg-preview-editor .milkdown a {
        color: var(--color-primary, #6366f1);
        text-decoration: underline;
      }

      .wysiwyg-preview-editor .milkdown hr {
        border: none;
        border-top: 1px solid var(--border-color, #e5e7eb);
        margin-top: 2em;
        margin-bottom: 2em;
      }

      .wysiwyg-preview-editor .milkdown table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 1em;
        margin-bottom: 1em;
      }

      .wysiwyg-preview-editor .milkdown th,
      .wysiwyg-preview-editor .milkdown td {
        border: 1px solid var(--border-color, #e5e7eb);
        padding: 8px 12px;
        text-align: left;
      }

      .wysiwyg-preview-editor .milkdown th {
        background-color: var(--color-background-light, #f9fafb);
        font-weight: 600;
      }
    `;
    document.head.appendChild(style);
  }
}

export default WysiwygPreviewEditor;
